import { useState } from 'react';
import { FormGroup, Input } from 'reactstrap';
import { UnitConfig } from '@g17eco/types/survey';
import { useAppSelector } from '@reducers/index';
import { FeaturePermissions } from '@services/permissions/FeaturePermissions';
import { getCheckOptions } from '@utils/survey';
import { updateFn } from '@g17eco/molecules/form';
import { NoteInstructionCollapseContainer } from '@features/note-instructions/NoteInstructionContainer';
import { SurveyQuestionCollapseForm } from '@features/survey-configuration/partials/SurveyQuestionForm';
import { SurveyUnitsAndCurrencyCollapse } from '@features/units-currency-settings';
import { TemplateConfigFormData } from '../types';
import { SURVEY } from '@constants/terminology';
import { getRootConfig } from '@selectors/globalData';

interface SurveySettingsProps {
  updateForm: updateFn;
  form: TemplateConfigFormData;
  isCreating: boolean;
  templateId: string;
}

export const TemplateSettings = (props: SurveySettingsProps) => {
  const { updateForm, templateId, form, isCreating } = props;
  const [unitConfig, setUnitConfig] = useState<UnitConfig>();

  const rootConfig = useAppSelector(getRootConfig);
  const canSetNoteInstructions = FeaturePermissions.canSetNoteInstructions(rootConfig);
  const canAccessVerification = FeaturePermissions.canAccessVerification(rootConfig);
  const checkOptions = getCheckOptions(canAccessVerification);

  const handleSetUnitConfig = (unitConfig: UnitConfig) => {
    updateForm({ code: 'unitConfig', value: unitConfig });
    setUnitConfig(unitConfig);
  };

  const toggleUseInitiativeSettings = () => {
    updateForm({ code: 'useInitiativeSettings', value: !form.useInitiativeSettings });
  };

  return (
    <>
      <div className='mt-4'>
        <h5>Template name*</h5>
        <Input
          placeholder={`e.g. Global 2022 CSR ${SURVEY.CAPITALIZED_SINGULAR}`}
          onChange={(el) => updateForm({ code: 'name', value: el.target.value })}
          defaultValue={form.name}
          data-testid='survey-template-name-input'
        />
      </div>
      <SurveyQuestionCollapseForm
        disabled={false}
        isCheckboxDisabled={false}
        checkOptions={checkOptions}
        form={form}
        updateForm={updateForm}
      />
      <FormGroup className='mt-4' disabled={false}>
        <SurveyUnitsAndCurrencyCollapse
          isCreating={isCreating}
          unitConfig={form.unitConfig ?? unitConfig}
          setUnitConfig={handleSetUnitConfig}
          title={`Default ${SURVEY.ADJECTIVE} units`}
          useInitiativeSettings={form.useInitiativeSettings}
          toggleUseInitiativeSettings={toggleUseInitiativeSettings}
          warningText={
            <>
              <b>WARNING:</b> Updating default units will only affect future {SURVEY.PLURAL}.
            </>
          }
        />
        <NoteInstructionCollapseContainer
          editorId={templateId}
          canSetNoteInstructions={canSetNoteInstructions}
          updateForm={updateForm}
          noteInstructionsEditorState={form.noteInstructionsEditorState}
          noteInstructions={form.noteInstructions}
        />
      </FormGroup>
    </>
  );
};
